import { CustomTableHeader, CustomTableItems, LabResult } from '@/@types';
import { CustomTable, ResultPageHeader } from '@/components';
import { StringHelper } from '@/helper';
import { useMemo } from 'react';
import { StatusPill } from '../statusPill/statusPill';

const tableHeaders: CustomTableHeader[] = [
  {
    label: 'Biomarker',
    key: 'analyteNameHtml',
    sortKey: 'analyteName',
    tooltip:
      'Biomarker names here reflect your original lab results, while the Lab trends page uses standardized names to combine results from multiple labs.',
  },
  {
    label: 'Range',
    key: 'referenceRange',
    width: 200,
  },
  {
    label: 'Result',
    key: 'value',
    width: 200,
  },
  {
    label: 'Status',
    key: 'resultStatus',
    width: 200,
    sortKey: 'status',
    tooltip:
      'The status reflects your original lab results, while the Lab trends page uses Atria optimal ranges to determine your status for key biomarkers.',
  },
];

type Props = {
  labResult: LabResult;
};

export function LabResultDetailsCard({ labResult }: Props) {
  const analytesTableItems: CustomTableItems[] = useMemo(() => {
    return labResult.observations
      .filter((observation) => observation.resultsStatus !== 'cancelled')
      .map(({ note, ...observation }) => ({
        ...observation,
        resultStatus: (
          <StatusPill status={observation.status} className='w-fit bg-neue-stone-300' />
        ),
        analyteNameHtml: StringHelper.firstLetterUppercase(observation.analyteName),
        id: observation.analyteId,
        value: observation.value ? `${observation.value} ${observation?.units || ''}`.trim() : '',
        referenceRange:
          !observation.referenceRange || observation.referenceRange === '*see table'
            ? ''
            : observation.referenceRange,
        actions: {
          ...(!!note && { expand: <pre>{note}</pre> }),
        },
      }));
  }, [labResult]);

  return (
    <>
      <ResultPageHeader result={labResult} />

      {labResult?.observations?.length > 0 && (
        <CustomTable
          sortable={['analyteNameHtml', 'resultStatus']}
          headers={tableHeaders}
          items={analytesTableItems}
        />
      )}
    </>
  );
}
